import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkBook } from 'xlsx';

import {
  isPasswordProtectedError,
  NotSpreadsheetError,
  readSpreadsheetFile,
} from '@feathr/blackbox';

export interface IUseSpreadsheetUploadReturn {
  cancelUpload: () => void;
  readUpload: (file: File, onWorkbook: (workbook: WorkBook) => void) => Promise<void>;
  uploadError?: string;
}

function fileExtension(file: File): string | undefined {
  const dot = file.name.lastIndexOf('.');
  return dot === -1 ? undefined : file.name.slice(dot + 1).toLowerCase();
}

// Reports describe the file, never its bytes: a headerless list starts with a customer's address.
function reportUnreadable(error: unknown, file: File): void {
  if (error instanceof DOMException || isPasswordProtectedError(error)) {
    return;
  }
  const details = error instanceof NotSpreadsheetError ? error.details : undefined;
  Sentry.captureException(error, {
    level: 'warning',
    tags: { feature: 'import-upload', rule: details?.rule },
    extra: { ...details, extension: fileExtension(file), size: file.size, type: file.type },
  });
}

function useSpreadsheetUpload(dropFile: () => void): IUseSpreadsheetUploadReturn {
  const { t } = useTranslation();
  const [uploadError, setUploadError] = useState<string>();
  const latestUpload = useRef(0);

  // A read that finishes after the step closes must not change the importer the next step uses.
  useEffect(() => {
    return (): void => {
      latestUpload.current += 1;
    };
  }, []);

  function rejectFile(): void {
    dropFile();
    setUploadError(
      t(
        "We couldn't read this file. Check that it's a .csv or .xlsx file without a password, then upload it again.",
      ),
    );
  }

  function cancelUpload(): void {
    latestUpload.current += 1;
    setUploadError(undefined);
  }

  function readUpload(file: File, onWorkbook: (workbook: WorkBook) => void): Promise<void> {
    latestUpload.current += 1;
    const upload = latestUpload.current;

    function handleWorkbook(workbook: WorkBook): void {
      if (upload !== latestUpload.current) {
        return;
      }
      try {
        onWorkbook(workbook);
        setUploadError(undefined);
      } catch (error) {
        Sentry.captureException(error, { tags: { feature: 'import-upload' } });
        rejectFile();
      }
    }

    function handleUnreadable(error: unknown): void {
      if (upload === latestUpload.current) {
        reportUnreadable(error, file);
        rejectFile();
      }
    }

    return readSpreadsheetFile(file).then(handleWorkbook, handleUnreadable);
  }

  return { cancelUpload, readUpload, uploadError };
}

export default useSpreadsheetUpload;
